import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CostFormDialog from "@/components/CostFormDialog";
import CostStatementEmailDialog from "@/components/CostStatementEmailDialog";
import { openFile } from "@/lib/files";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { DollarSign, Plus, Pencil, Trash2, Paperclip, FileDown } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAY_STYLE = {
  unpaid: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
  reimbursed: "bg-blue-50 text-blue-700",
};
const payLabel = (s) => ({ unpaid: "Unpaid", paid: "Paid", reimbursed: "Reimbursed" }[s] || s);

export function ClientCosts({ clientId, clientName, clientEmail }) {
  const { user } = useAuth();
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/costs`).then((r) => { setCosts(r.data.costs); setSummary(r.data.summary); }).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/costs/meta").then((r) => setCategories(r.data.categories)).catch(() => {}); }, []);

  const confirmDelete = async () => {
    try { await api.delete(`/costs/${deleteTarget.id}`); toast.success("Cost deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  const openInvoice = async (att) => {
    try { await openFile(att.file_id, att.filename); } catch { toast.error("Could not open invoice"); }
  };

  const downloadStatement = async (format, scope) => {
    try {
      const res = await api.get(`/clients/${clientId}/costs/export`, {
        params: { format, scope }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cost_statement.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not export statement");
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="client-costs-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-gray-400" /> Costs
        </h3>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="cost-statement-button"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-[#002FA7] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
                <FileDown className="w-3.5 h-3.5" /> Statement
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem data-testid="statement-email" onClick={() => setEmailOpen(true)}>Email statement…</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Billable costs only</DropdownMenuLabel>
              <DropdownMenuItem data-testid="statement-billable-pdf" onClick={() => downloadStatement("pdf", "billable")}>Download PDF</DropdownMenuItem>
              <DropdownMenuItem data-testid="statement-billable-csv" onClick={() => downloadStatement("csv", "billable")}>Download CSV</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">All costs</DropdownMenuLabel>
              <DropdownMenuItem data-testid="statement-all-pdf" onClick={() => downloadStatement("pdf", "all")}>Download PDF</DropdownMenuItem>
              <DropdownMenuItem data-testid="statement-all-csv" onClick={() => downloadStatement("csv", "all")}>Download CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="client-add-cost-button"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:text-[#002480] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add cost
          </button>
        </div>
      </div>

      {summary && summary.count > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-4" data-testid="client-costs-summary">
          <div className="bg-[#002FA7]/5 rounded-sm p-3">
            <p className="font-heading text-xl font-extrabold text-[#002FA7]" data-testid="costs-total">{fmt(summary.total)}</p>
            <p className="text-xs font-semibold text-gray-500">Total costs</p>
          </div>
          <div className="bg-gray-50 rounded-sm p-3">
            <p className="font-heading text-xl font-extrabold text-gray-900">{fmt(summary.billable_total)}</p>
            <p className="text-xs font-semibold text-gray-500">Billable to client</p>
          </div>
        </div>
      )}

      {costs.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-sm" data-testid="client-costs-empty">No costs recorded yet.</p>
      ) : (
        <div className="space-y-2" data-testid="client-costs-list">
          {costs.map((c) => (
            <div key={c.id} data-testid={`cost-row-${c.id}`}
              className="border border-gray-100 rounded-sm p-3 flex items-start justify-between gap-3 hover:border-gray-200 transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 text-sm">{fmt(c.amount)}</span>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{c.category}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${PAY_STYLE[c.payment_status] || ""}`}>{payLabel(c.payment_status)}</span>
                  {c.billable && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">Billable</span>}
                </div>
                <p className="text-sm text-gray-700 mt-1 truncate">{c.description}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {c.date}{c.vendor ? ` · ${c.vendor}` : ""}
                </p>
                {c.attachment && (
                  <button onClick={() => openInvoice(c.attachment)} data-testid={`cost-invoice-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:underline mt-1">
                    <Paperclip className="w-3 h-3" /> {c.attachment.filename}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`cost-edit-${c.id}`}
                  className="text-gray-400 hover:text-[#002FA7] p-1"><Pencil className="w-4 h-4" /></button>
                {user?.role === "admin" && (
                  <button onClick={() => setDeleteTarget(c)} data-testid={`cost-delete-${c.id}`}
                    className="text-gray-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CostFormDialog open={dialogOpen} onOpenChange={setDialogOpen} clientId={clientId} cost={editing}
        categories={categories} onSaved={load} />

      <CostStatementEmailDialog open={emailOpen} onOpenChange={setEmailOpen} clientId={clientId}
        clientName={clientName} clientEmail={clientEmail} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-cost-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete cost?</AlertDialogTitle>
            <AlertDialogDescription>This cost entry will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-cost-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
